import React, { useState, useRef, useEffect } from "react";
import { OnyxMascotFace } from "../../shared/OnyxMascot";
import { useI18n, localeLabels, type Locale } from "../i18n";

export function Header() {
  const { locale, setLocale, t } = useI18n();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 w-full z-50 bg-[#0a0a12]/80 backdrop-blur-md border-b border-[#2a2040]">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 group hover:opacity-90 transition-opacity">
          <div className="transition-transform group-hover:scale-110">
            <OnyxMascotFace size={32} />
          </div>
          <span className="text-lg font-semibold text-white onyx-title">
            ONYX <span className="text-[#c0a0e0] font-normal text-sm">AI</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          <a href="/features/" className="text-sm text-[#8a7a9a] hover:text-white transition-colors">
            {t("nav.features")}
          </a>
          <a href="#how-it-works" className="text-sm text-[#8a7a9a] hover:text-white transition-colors">
            {t("nav.howItWorks")}
          </a>
          <a href="/pricing/" className="text-sm text-[#8a7a9a] hover:text-white transition-colors">
            {t("nav.pricing")}
          </a>
          <a href="/blog" className="text-sm text-[#8a7a9a] hover:text-white transition-colors">
            {t("footer.blog")}
          </a>
          <a href="/docs" className="text-sm text-[#8a7a9a] hover:text-white transition-colors">
            {t("footer.docs")}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          {/* Language Switcher */}
          <div className="onyx-lang-switcher" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[#8a7a9a] hover:text-white border border-[#2a2040] hover:border-[#3a3060] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 003 12c0-1.605.42-3.113 1.157-4.418" />
              </svg>
              {localeLabels[locale]}
              <svg className={`w-3 h-3 transition-transform ${langOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>

            {langOpen && (
              <div className="onyx-lang-menu">
                {(Object.keys(localeLabels) as Locale[]).map((loc) => (
                  <button
                    key={loc}
                    className={`onyx-lang-option ${locale === loc ? "active" : ""}`}
                    onClick={() => { setLocale(loc); setLangOpen(false); }}
                  >
                    {localeLabels[loc]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="/portal"
            className="px-4 py-2 text-[#8a7a9a] hover:text-white text-sm font-medium transition-colors hidden sm:inline-block"
          >
            {t("nav.login")}
          </a>
          <a
            href="/signup"
            className="px-4 py-2 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white text-sm font-medium rounded-lg transition-colors"
          >
            Get Started Free
          </a>
        </div>
      </div>
    </header>
  );
}
